import { Link } from "react-router-dom";
import { AlertCircle, Calendar, Clock, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

interface OperationalStripProps {
  stats: {
    publishedThisWeek: number;
    scheduledUpcoming: number;
    failedLast30d: number;
    queuedNow: number;
  };
  brandSlug: string;
}

interface TileProps {
  label: string;
  value: number;
  href?: string;
  icon: React.ReactNode;
  tone?: "default" | "danger";
}

function Tile({ label, value, href, icon, tone = "default" }: TileProps) {
  const inner = (
    <div
      className={`rounded-lg border bg-card p-4 transition ${
        href ? "hover:bg-muted/60" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span
          className={
            tone === "danger" && value > 0
              ? "text-red-500"
              : "text-muted-foreground"
          }
        >
          {icon}
        </span>
      </div>
      <div
        className={`mt-2 text-2xl font-semibold ${
          tone === "danger" && value > 0 ? "text-red-600" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

export function OperationalStrip({ stats, brandSlug }: OperationalStripProps) {
  const { t } = useTranslation("analytics");
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tile
        label={t("operational.publishedThisWeek")}
        value={stats.publishedThisWeek}
        icon={<Send className="h-4 w-4" />}
      />
      <Tile
        label={t("operational.scheduledUpcoming")}
        value={stats.scheduledUpcoming}
        href={`/${brandSlug}/calendar`}
        icon={<Calendar className="h-4 w-4" />}
      />
      <Tile
        label={t("operational.failedLast30d")}
        value={stats.failedLast30d}
        href={`/${brandSlug}/calendar?filter=failed`}
        icon={<AlertCircle className="h-4 w-4" />}
        tone="danger"
      />
      <Tile
        label={t("operational.queuedNow")}
        value={stats.queuedNow}
        icon={<Clock className="h-4 w-4" />}
      />
    </div>
  );
}
